import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '../Auth/AuthProvider';
import { AvatarImage } from '../UI/AvatarImage';
import NotificationSystem from '../NotificationSystem';
import './Navigation.css';
import { getFunctions, httpsCallable } from 'firebase/functions';
import HistoryTimes from '../HistoryTimes';
import { db, storage } from '../../firebase';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { buildPublicAppUrl } from '../../utils/publicAppUrl';

export const Navigation = ({
  currentView,
  onViewChange,
  onSignOut,
  onAvatarClick,
  onGoToAdmin,
  onGoToWelcome,
  onNavigateToProfile,
  onNavigateToRoutine,
  onLogoDisappear,
  onLogoAppear,
  onOpenAIAssistant,
}) => {
  const { user, userProfileData, isAdmin } = useAuth();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showToast, setShowToast] = useState(false);
  const dropdownRef = useRef(null);
  const hamburgerButtonRef = useRef(null);
  const userMenuButtonRef = useRef(null);
  const desktopDropdownPortalRef = useRef(null);
  const [mobileDropdownPos, setMobileDropdownPos] = useState({ top: 72, right: 12 });
  const [desktopDropdownPos, setDesktopDropdownPos] = useState({ top: 72, right: 12 });

  // Estado para top10
  const [topUsers, setTopUsers] = useState([]);

  // Estado para subida de historia desde navbar
  const fileInputRef = useRef();
  const [uploadingStory, setUploadingStory] = useState(false);
  const [uploadError, setUploadError] = useState('');

  // TODO: Reemplazar por lógica real de historias de usuarios seguidos
  const mockStories = undefined; // Si quieres forzar la vista, usa: mockStories = [{...}];

  // Estado para controlar la visibilidad del logo de Ratafit
  const [showLogo, setShowLogo] = useState(true);

  // Estado para mostrar/ocultar historias
  const [showStories, setShowStories] = useState(() => {
    const stored = localStorage.getItem('showStories');
    return stored === null ? false : stored === 'true';
  });
  const handleStoriesToggle = (e) => {
    setShowStories(e.target.checked);
    localStorage.setItem('showStories', e.target.checked);
  };

  // Efecto: al abrir el menú hamburguesa ocultar logo héroe; al cerrarlo mostrarlo de nuevo
  const prevSidebarOpen = useRef(isSidebarOpen);
  useEffect(() => {
    if (!prevSidebarOpen.current && isSidebarOpen) {
      if (onLogoDisappear) onLogoDisappear();
    }
    if (prevSidebarOpen.current && !isSidebarOpen) {
      if (onLogoAppear) onLogoAppear();
    }
    prevSidebarOpen.current = isSidebarOpen;
  }, [isSidebarOpen, onLogoDisappear, onLogoAppear]);

  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth <= 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Posicionar el dropdown móvil en el viewport (portal), relativo al botón hamburguesa.
  useEffect(() => {
    if (!isMobile || !isSidebarOpen) return;
    if (!hamburgerButtonRef.current) return;

    const compute = () => {
      const rect = hamburgerButtonRef.current.getBoundingClientRect();
      const top = Math.round(rect.bottom + 8);
      const right = Math.max(8, Math.round(window.innerWidth - rect.right));
      setMobileDropdownPos({ top, right });
    };

    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, { passive: true });
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute);
    };
  }, [isMobile, isSidebarOpen]);

  // Posicionar el dropdown de escritorio (portal) bajo el botón de usuario.
  useEffect(() => {
    if (isMobile || !isDropdownOpen) return;
    if (!userMenuButtonRef.current) return;

    const compute = () => {
      const rect = userMenuButtonRef.current.getBoundingClientRect();
      const top = Math.round(rect.bottom + 8);
      const right = Math.max(8, Math.round(window.innerWidth - rect.right));
      setDesktopDropdownPos({ top, right });
    };

    compute();
    window.addEventListener('resize', compute);
    window.addEventListener('scroll', compute, { passive: true });
    return () => {
      window.removeEventListener('resize', compute);
      window.removeEventListener('scroll', compute);
    };
  }, [isMobile, isDropdownOpen]);

  // Cerrar menú móvil o dropdown de escritorio con Escape.
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      if (isSidebarOpen) setIsSidebarOpen(false);
      if (!isMobile && isDropdownOpen) setIsDropdownOpen(false);
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [isSidebarOpen, isDropdownOpen, isMobile]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      const inTrigger = dropdownRef.current && dropdownRef.current.contains(event.target);
      const inDesktopPortal = desktopDropdownPortalRef.current && desktopDropdownPortalRef.current.contains(event.target);
      if (!inTrigger && !inDesktopPortal) {
        setIsDropdownOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    const loadTopUsers = async () => {
      try {
        const functions = getFunctions();
        const debugForceUpdate = httpsCallable(functions, 'debugForceUpdate');
        const result = await debugForceUpdate();
        const { success, debugInfo } = result.data;
        if (success && debugInfo && Array.isArray(debugInfo)) {
          const users = debugInfo
            .filter(u => u.routines > 0)
            .slice(0, 10)
            .map((u) => u.id);
          setTopUsers(users);
        }
      } catch (error) {
        setTopUsers([]);
      }
    };
    if (user) loadTopUsers();
  }, [user]);

  useEffect(() => {
    const handleHideLogo = () => setShowLogo(false);
    const handleShowLogo = () => setShowLogo(true);
    window.addEventListener('hideRatafitLogo', handleHideLogo);
    window.addEventListener('showRatafitLogo', handleShowLogo);
    return () => {
      window.removeEventListener('hideRatafitLogo', handleHideLogo);
      window.removeEventListener('showRatafitLogo', handleShowLogo);
    };
  }, []);

  const isTop10 = user && topUsers.includes(user.uid);

  const toggleDropdown = () => setIsDropdownOpen(!isDropdownOpen);
  const toggleSidebar = () => setIsSidebarOpen(!isSidebarOpen);
  const closeSidebar = () => setIsSidebarOpen(false);

  const handleViewChange = (view) => {
    onViewChange(view);
    setIsDropdownOpen(false);
    setIsSidebarOpen(false);
  };

  const getDisplayName = () => {
    return userProfileData?.nickname ||
      userProfileData?.displayName ||
      user?.displayName ||
      user?.email?.split('@')[0] ||
      'Usuario';
  };

  const copyProfileURL = async () => {
    try {
      const url = buildPublicAppUrl(`/profile/${user.uid}`);
      await navigator.clipboard.writeText(url);
      setShowToast(true);
      setTimeout(() => setShowToast(false), 3000);
      setIsDropdownOpen(false);
      setIsSidebarOpen(false);
    } catch (error) {
      console.error('Error:', error);
    }
  };

  // Lógica para subir historia desde navbar
  const handleAddStoryClick = () => {
    if (!user) return;
    fileInputRef.current.click();
  };
  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file || !user) return;
    setUploadingStory(true);
    setUploadError('');
    try {
      // Subir a Storage en la ruta /stories/{userId}/{fileName}
      const storageRef = ref(storage, `stories/${user.uid}/${file.name}`);
      await uploadBytes(storageRef, file);
      const imageUrl = await getDownloadURL(storageRef);
      const storyDoc = doc(db, 'stories', user.uid);
      await setDoc(storyDoc, {
        userId: user.uid,
        userName: userProfileData?.nickname || userProfileData?.displayName || user.displayName || user.email,
        userAvatar: userProfileData?.customPhotoURL || userProfileData?.photoURL || user.photoURL,
        imageUrl,
        createdAt: serverTimestamp(),
      });
      setUploadingStory(false);
      alert('¡Historia subida exitosamente!');
    } catch (err) {
      setUploadError('Error al subir la historia. Intenta de nuevo.');
      setUploadingStory(false);
    }
  };

  return (
    <>
      <nav className="nav-app-navigation">
        {/* Brand */}
        <div className={`nav-brand${!showLogo ? ' nav-logo-hidden' : ''}`}>
          <h1
            className="nav-app-title"
            onClick={() => (onGoToWelcome ? onGoToWelcome() : handleViewChange('welcome'))}
            style={{ cursor: 'pointer' }}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); (onGoToWelcome ? onGoToWelcome() : handleViewChange('welcome')); } }}
          >
            Ratafit
          </h1>
          {/* Botón + para subir historia eliminado */}
        </div>

        {/* Centro: navegación y notificaciones */}
        <div className="nav-center">
          {/* Desktop Navigation Menu */}
          {!isMobile && user && (
            <div className="nav-menu">
              <button
                className={`nav-button${currentView === 'routines' ? ' nav-active' : ''}`}
                onClick={() => handleViewChange('routines')}
              >
                💪 Rutinas
              </button>
              <button
                className={`nav-button${currentView === 'diets' ? ' nav-active' : ''}`}
                onClick={() => handleViewChange('diets')}
              >
                🍽️ Dietas
              </button>
              {!userProfileData?.isPremium && (
                <button
                  className={`nav-button${currentView === 'premium' ? ' nav-active' : ''}`}
                  onClick={() => handleViewChange('premium')}
                >
                  ⭐ Premium
                </button>
              )}
              <button
                className={`nav-button${currentView === 'search' ? ' nav-active' : ''}`}
                onClick={() => handleViewChange('search')}
              >
                🔍 Explorar
              </button>
              <button
                className={`nav-button${currentView === 'progress' ? ' nav-active' : ''}`}
                onClick={() => handleViewChange('progress')}
              >
                📊 Progreso
              </button>
              {onOpenAIAssistant && (
                <button
                  type="button"
                  className="nav-button nav-button-ai"
                  onClick={onOpenAIAssistant}
                  aria-label="Abrir asistente IA"
                >
                  🤖 Asistente
                </button>
              )}
            </div>
          )}

          {/* Mobile Navigation Buttons */}
          {isMobile && user && (
            <div className="nav-mobile-group">
              <button
                className={`nav-mobile-button${currentView === 'routines' ? ' nav-active' : ''}`}
                onClick={() => handleViewChange('routines')}
              >
                <span className="nav-mobile-icon">💪</span>
                Rutinas
              </button>
              <button
                className={`nav-mobile-button${currentView === 'diets' ? ' nav-active' : ''}`}
                onClick={() => handleViewChange('diets')}
              >
                <span className="nav-mobile-icon">🍽️</span>
                Dietas
              </button>
            </div>
          )}

          {/* Sistema de notificaciones */}
          {user && (
            <div className="nav-notification-system">
              <NotificationSystem
                onNavigateToProfile={onNavigateToProfile}
                onNavigateToRoutine={onNavigateToRoutine}
                onNotificationBellClick={onLogoDisappear}
                onNotificationPanelClose={onLogoAppear}
              />
            </div>
          )}
        </div>

        {/* Acciones */}
        <div className="nav-actions">
          {user ? (
            <>
              {/* Desktop: menú de usuario con avatar */}
              {!isMobile && (
                <div className="nav-user-menu" ref={dropdownRef}>
                  <div
                    ref={userMenuButtonRef}
                    className="nav-user-info"
                    onClick={toggleDropdown}
                    tabIndex={0}
                    role="button"
                    aria-expanded={isDropdownOpen}
                    style={{ position: 'relative' }}
                  >
                    {/* Badge de trofeo si es top10 */}
                    {isTop10 && (
                      <div
                        className="ratafit-us-rank-badge ratafit-us-rank-top"
                        title="Usuario destacado en el Top 10 de la comunidad"
                      >🏆</div>
                    )}
                    <AvatarImage
                      src={userProfileData?.customPhotoURL || user?.photoURL}
                      alt={getDisplayName()}
                      size="small"
                      className="nav-user-avatar-small"
                    />
                    <span className="nav-user-name-small">{getDisplayName()}</span>
                    <span className={`nav-arrow${isDropdownOpen ? ' nav-open' : ''}`}>▼</span>
                  </div>
                </div>
              )}

              {/* Mobile: menú hamburguesa - SIEMPRE visible cuando hay usuario y es móvil */}
              {isMobile && (
                <div className="nav-hamburger-menu" ref={dropdownRef}>
                  <button
                    className={`nav-hamburger-button${isSidebarOpen ? ' nav-open' : ''}`}
                    onClick={toggleSidebar}
                    aria-expanded={isSidebarOpen}
                    aria-label="Menú de navegación"
                    style={{ position: 'relative' }}
                    ref={hamburgerButtonRef}
                  >
                    <div className="nav-hamburger-icon">
                      <div className="nav-hamburger-line"></div>
                      <div className="nav-hamburger-line"></div>
                      <div className="nav-hamburger-line"></div>
                    </div>
                  </button>
                </div>
              )}
            </>
          ) : null}
        </div>

        {/* Toast de confirmación */}
        {showToast && (
          <div className="nav-toast">
            <span>✅</span>
            <span>¡Enlace copiado!</span>
          </div>
        )}
      </nav>
      {/* Dropdown de escritorio en portal (por encima de Welcome y otros overlays) */}
      {!isMobile && user && isDropdownOpen && createPortal(
        <div
          ref={desktopDropdownPortalRef}
          className="nav-dropdown nav-dropdown--desktop"
          style={{
            top: desktopDropdownPos.top,
            right: desktopDropdownPos.right,
            left: 'auto',
            minWidth: 220
          }}
          onClick={(e) => e.stopPropagation()}
        >
          <div className="nav-menu-dropdown">
            <div
              className="nav-mobile-user-info nav-mobile-user-info--clickable"
              role="button"
              tabIndex={0}
              onClick={() => { handleViewChange('myProfile'); setIsDropdownOpen(false); }}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleViewChange('myProfile'); setIsDropdownOpen(false); } }}
            >
              <AvatarImage
                src={userProfileData?.customPhotoURL || user?.photoURL}
                alt={getDisplayName()}
                size="medium"
              />
              <div className="nav-user-mobile-info">
                <span className="nav-user-name-medium">{getDisplayName()}</span>
                <span className="nav-user-email-small">{user.email}</span>
                {isAdmin && <span className="nav-admin-badge-mobile">👑 Admin</span>}
              </div>
            </div>
            <div className="nav-divider"></div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={showStories}
                  onChange={handleStoriesToggle}
                  style={{ accentColor: '#ffd700', width: 18, height: 18 }}
                />
                <span style={{ color: '#ffd700', fontWeight: 600 }}>Mostrar historias</span>
              </label>
            </div>
            <div className="nav-divider"></div>
            {onOpenAIAssistant && (
              <button type="button" className="nav-button" onClick={() => { onOpenAIAssistant(); setIsDropdownOpen(false); }}>
                🤖 Asistente IA
              </button>
            )}
            <button className="nav-button" onClick={() => { handleViewChange('myProfile'); setIsDropdownOpen(false); }}>
              👤 Mi Perfil
            </button>
            <button className="nav-button" onClick={() => { copyProfileURL(); setIsDropdownOpen(false); }}>
              📋 Copiar perfil
            </button>
            {isAdmin && (
              <button className="nav-button nav-admin-button" onClick={() => { onGoToAdmin(); setIsDropdownOpen(false); }}>
                ⚙️ Admin
              </button>
            )}
            <div className="nav-divider"></div>
            <button className="nav-button" onClick={() => { onSignOut(); setIsDropdownOpen(false); }} style={{ color: '#ff6b7a' }}>
              🚪 Cerrar Sesión
            </button>
          </div>
        </div>,
        document.body
      )}
      {/* Menú móvil renderizado en portal para evitar overlays que bloqueen clicks */}
      {isMobile && user && isSidebarOpen && createPortal(
        <>
          <div className="nav-overlay nav-overlay--portal" onClick={closeSidebar} />
          <div
            className="nav-dropdown nav-dropdown--mobile"
            style={{ top: mobileDropdownPos.top, right: mobileDropdownPos.right, left: 'auto', minWidth: 220 }}
            onClick={(e) => e.stopPropagation()}
          >
            <div className="nav-menu-dropdown">
              <div
                className="nav-mobile-user-info nav-mobile-user-info--clickable"
                style={{ position: 'relative' }}
                role="button"
                tabIndex={0}
                onClick={() => handleViewChange('myProfile')}
                onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); handleViewChange('myProfile'); } }}
              >
                {/* Badge de trofeo si es top10 */}
                {isTop10 && (
                  <div
                    className="ratafit-us-rank-badge ratafit-us-rank-top"
                    title="Usuario destacado en el Top 10 de la comunidad"
                  >🏆</div>
                )}
                <AvatarImage
                  src={userProfileData?.customPhotoURL || user?.photoURL}
                  alt={getDisplayName()}
                  size="medium"
                />
                <div className="nav-user-mobile-info">
                  <span className="nav-user-name-medium">{getDisplayName()}</span>
                  <span className="nav-user-email-small">{user.email}</span>
                  {isAdmin && <span className="nav-admin-badge-mobile">👑 Admin</span>}
                </div>
              </div>

              {/* Switch para mostrar/ocultar historias */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, margin: '12px 0' }}>
                <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={showStories}
                    onChange={handleStoriesToggle}
                    style={{ accentColor: '#ffd700', width: 18, height: 18 }}
                  />
                  <span style={{ color: '#ffd700', fontWeight: 600 }}>Mostrar historias</span>
                </label>
              </div>

              <div className="nav-divider"></div>

              {!userProfileData?.isPremium && (
                <button
                  className="nav-button"
                  onClick={() => handleViewChange('premium')}
                >
                  ⭐ Premium
                </button>
              )}
              <button
                className="nav-button"
                onClick={() => handleViewChange('progress')}
              >
                📊 Mi Progreso
              </button>
              <button
                className="nav-button"
                onClick={() => handleViewChange('search')}
              >
                🔍 Explorar
              </button>
              {onOpenAIAssistant && (
                <button type="button" className="nav-button" onClick={() => { onOpenAIAssistant(); closeSidebar(); }}>
                  🤖 Asistente IA
                </button>
              )}
              <button
                className="nav-button"
                onClick={() => handleViewChange('myProfile')}
              >
                👤 Mi Perfil
              </button>
              <button
                className="nav-button"
                onClick={copyProfileURL}
              >
                📋 Compartir mi perfil
              </button>

              {isAdmin && (
                <>
                  <div className="nav-divider"></div>
                  <button
                    className="nav-button nav-admin-button"
                    onClick={onGoToAdmin}
                  >
                    ⚙️ Dashboard de Admin
                  </button>
                </>
              )}

              <div className="nav-divider"></div>

              <button
                className="nav-button"
                onClick={onSignOut}
                style={{ color: '#ff6b7a' }}
              >
                🚪 Cerrar Sesión
              </button>
            </div>
          </div>
        </>,
        document.body
      )}
      {/* Barra de historias tipo Instagram, siempre visible debajo del navbar principal */}
      {showStories && <HistoryTimes />}
    </>
  );
};